import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import type {
  SignInResult,
  SignInWithGoogleOptions,
  SignInWithOAuthOptions,
} from "@capacitor-firebase/authentication";

/** Profile shape sent to `loginBySocial("google", …)` */
export interface FirebaseGoogleSocialProfile {
  email: string | null;
  familyName: string | null;
  givenName: string | null;
  id: string | null;
  name: string | null;
  imageUrl: string | null;
}

export interface FirebaseGoogleSignInPayload {
  idToken: string;
  profile: FirebaseGoogleSocialProfile;
}

/** Same payload shape as Google: Firebase Auth ID token + profile for `loginBySocial("apple", …)`. */
export type FirebaseAppleSignInPayload = FirebaseGoogleSignInPayload;

@Injectable({
  providedIn: "root",
})
export class FirebaseAuthService {
  /** Set on web during APP_INITIALIZER from `getRedirectResult` before any router guard runs. */
  private pendingWebGoogleRedirectPayload: FirebaseGoogleSignInPayload | null = null;
  private readonly logPrefix = "[FirebaseAuthService]";
  private signOutPromise: Promise<void> | null = null;
  /**
   * True when the user closed the Google UI or the flow was dismissed (not a server/config failure).
   */
  isUserCancelledGoogleSignIn(error: unknown): boolean {
    const msg = this.extractErrorMessage(error);
    return (
      /cancel/i.test(msg) ||
      /closed the flow/i.test(msg) ||
      /user dismissed/i.test(msg) ||
      /12501/i.test(msg) ||
      /popup-closed-by-user/i.test(msg) ||
      /auth\/popup-closed-by-user/i.test(msg)
    );
  }

  /** Apple / OAuth flows reuse the same user-dismissal heuristics as Google. */
  isUserCancelledAppleSignIn(error: unknown): boolean {
    return this.isUserCancelledGoogleSignIn(error);
  }

  /**
   * Android Google Sign-In status 10 (DEVELOPER_ERROR): missing SHA-1/SHA-256 in Firebase,
   * wrong package name, or OAuth client mismatch.
   */
  isGoogleSignInDeveloperConfigError(error: unknown): boolean {
    if (typeof error === "object" && error !== null) {
      const o = error as Record<string, unknown>;
      for (const code of [o["code"], o["statusCode"], o["status"]]) {
        if (code === 10 || code === "10") {
          return true;
        }
      }
      const nested = o["error"];
      if (nested !== undefined && nested !== o) {
        if (this.isGoogleSignInDeveloperConfigError(nested)) {
          return true;
        }
      }
    }
    const msg = this.extractErrorMessage(error);
    let json = "";
    if (typeof error === "object" && error !== null) {
      try {
        json = JSON.stringify(error);
      } catch {
        /* ignore */
      }
    }
    const haystack = `${msg} ${json} ${String(error)}`;
    return (
      /DEVELOPER_ERROR/i.test(haystack) ||
      /ApiException:\s*10\b/i.test(haystack) ||
      /status\s*code:\s*10\b/i.test(haystack) ||
      /:\s*10\s*:/.test(haystack) ||
      /_\s*:\s*10\s*:/.test(haystack) ||
      /["']code["']\s*:\s*10\b/.test(haystack) ||
      /:\s*10\b(?:\s|:|$)/.test(haystack)
    );
  }

  private extractErrorMessage(error: unknown): string {
    if (error == null) {
      return "";
    }
    if (typeof error === "string") {
      return error;
    }
    if (error instanceof Error) {
      return `${error.name} ${error.message} ${error.stack ?? ""}`;
    }
    if (typeof error === "object") {
      const o = error as Record<string, unknown>;
      const parts: string[] = [];
      const message = o["message"];
      if (typeof message === "string") {
        parts.push(message);
      }
      const errorMessage = o["errorMessage"];
      if (typeof errorMessage === "string") {
        parts.push(errorMessage);
      }
      const code = o["code"];
      if (code !== undefined) {
        parts.push(String(code));
      }
      const nested = o["error"];
      if (nested != null && typeof nested !== "object") {
        parts.push(String(nested));
      } else if (nested !== undefined && nested !== o) {
        parts.push(this.extractErrorMessage(nested));
      }
      if (parts.length > 0) {
        return parts.join(" ");
      }
      try {
        return JSON.stringify(o);
      } catch {
        return String(error);
      }
    }
    return String(error);
  }

  /**
   * Run once at app bootstrap (web only), before Router navigates — so guards cannot strip the OAuth URL.
   */
  async captureWebGoogleRedirectOnBootstrap(): Promise<void> {
    if (Capacitor.getPlatform() !== "web") {
      return;
    }
    try {
      const loginResult = await FirebaseAuthentication.getRedirectResult();
      console.debug(
        `${this.logPrefix} getRedirectResult at bootstrap`,
        this.toDebugSignInSnapshot(loginResult)
      );
      const payload = await this.payloadFromSignInResult(loginResult);
      if (payload) {
        console.debug(
          `${this.logPrefix} cached redirect payload`,
          this.toDebugPayloadSnapshot(payload)
        );
        this.pendingWebGoogleRedirectPayload = payload;
      }
    } catch (e) {
      console.error("Google redirect bootstrap handling failed:", e);
    }
  }

  /**
   * After web `signInWithGoogle({ mode: 'redirect' })`, complete the flow on return (login page).
   */
  async consumeWebGoogleRedirectSignIn(): Promise<FirebaseGoogleSignInPayload | null> {
    if (Capacitor.getPlatform() !== "web") {
      return null;
    }
    const pending = this.pendingWebGoogleRedirectPayload;
    this.pendingWebGoogleRedirectPayload = null;
    if (pending) {
      return pending;
    }
    try {
      const loginResult = await FirebaseAuthentication.getRedirectResult();
      return await this.payloadFromSignInResult(loginResult);
    } catch (e) {
      console.error("Google redirect sign-in failed:", e);
      return null;
    }
  }

  /**
   * On deployed hosts, COOP often blocks Firebase’s popup; localhost gets popup via `angular.json` headers.
   */
  usesWebGoogleRedirectFlow(): boolean {
    return Capacitor.getPlatform() === "web" && !this.shouldUseWebGooglePopup();
  }

  private shouldUseWebGooglePopup(): boolean {
    if (typeof window === "undefined") {
      return false;
    }
    const h = window.location.hostname;
    return h === "localhost" || h === "127.0.0.1";
  }

  async signInWithGoogle(
    scopes: string[] = ["email", "profile"]
  ): Promise<FirebaseGoogleSignInPayload | null> {
    const platform = Capacitor.getPlatform();
    console.debug(`${this.logPrefix} signInWithGoogle start`, { platform, scopes });

    if (platform === "web") {
      const loginResult = await FirebaseAuthentication.signInWithGoogle(
        this.shouldUseWebGooglePopup()
          ? { mode: "popup", scopes }
          : { mode: "redirect", scopes }
      );
      console.debug(
        `${this.logPrefix} signInWithGoogle web result`,
        this.toDebugSignInSnapshot(loginResult)
      );
      return await this.payloadFromSignInResult(loginResult);
    }

    const options: SignInWithGoogleOptions =
      platform === "android"
        ? {
            useCredentialManager: false,
            scopes: [],
          }
        : { scopes };

    const loginResult = await FirebaseAuthentication.signInWithGoogle(options);
    console.debug(
      `${this.logPrefix} signInWithGoogle native result`,
      this.toDebugSignInSnapshot(loginResult)
    );
    return await this.payloadFromSignInResult(loginResult);
  }

  /**
   * Native + web: Firebase handles Apple OAuth, then we read a Firebase Auth ID token for the backend
   * (same issuer contract as {@link signInWithGoogle}).
   */
  async signInWithApple(): Promise<FirebaseAppleSignInPayload | null> {
    const platform = Capacitor.getPlatform();
    const scopes = ["email", "name"];
    console.debug(`${this.logPrefix} signInWithApple start`, { platform, scopes });

    // Web: always popup so we never mix Apple redirect completion with the Google-only resume path on login.
    const oauthOptions: SignInWithOAuthOptions =
      platform === "web" ? { mode: "popup", scopes } : { scopes };

    const loginResult = await FirebaseAuthentication.signInWithApple(oauthOptions);
    console.debug(
      `${this.logPrefix} signInWithApple result`,
      this.toDebugSignInSnapshot(loginResult)
    );
    return await this.payloadFromSignInResult(loginResult);
  }

  private async payloadFromSignInResult(
    loginResult: SignInResult
  ): Promise<FirebaseGoogleSignInPayload | null> {
    if (!loginResult.user) {
      return null;
    }
    // Backend expects a Firebase Auth ID token (securetoken.google.com issuer),
    // not the Google provider credential token (accounts.google.com issuer).
    const res = await FirebaseAuthentication.getIdToken({
      forceRefresh: false,
    });
    const token = res.token || null;
    if (!token) {
      console.warn(`${this.logPrefix} no idToken returned from FirebaseAuthentication.getIdToken`);
      return null;
    }
    const payload = {
      idToken: token,
      profile: this.mapSocialProfileFromSignInResult(loginResult),
    };
    console.debug(
      `${this.logPrefix} built social payload`,
      this.toDebugPayloadSnapshot(payload)
    );
    return payload;
  }

  /**
   * Clears the ephemeral Firebase session after social sign-in (Bob uses backend JWT).
   * Idempotent: concurrent calls share one native signOut.
   */
  async signOut(): Promise<void> {
    if (this.signOutPromise) {
      return this.signOutPromise;
    }
    this.signOutPromise = this.runSignOut();
    try {
      await this.signOutPromise;
    } finally {
      this.signOutPromise = null;
    }
  }

  private async runSignOut(): Promise<void> {
    try {
      const { user } = await FirebaseAuthentication.getCurrentUser();
      if (!user) {
        return;
      }
      await FirebaseAuthentication.signOut();
    } catch {
      /* Native plugin may log after sign-out; safe to ignore. */
    }
  }

  async getIdToken(forceRefresh = false): Promise<string | null> {
    try {
      const { token } = await FirebaseAuthentication.getIdToken({
        forceRefresh,
      });
      return token || null;
    } catch {
      return null;
    }
  }

  /** Maps Google / Apple (and similar) federated profiles into the backend `user` shape. */
  private mapSocialProfileFromSignInResult(
    result: SignInResult
  ): FirebaseGoogleSocialProfile {
    const u = result.user;
    if (!u) {
      return {
        email: null,
        familyName: null,
        givenName: null,
        id: null,
        name: null,
        imageUrl: null,
      };
    }
    const raw = result.additionalUserInfo?.profile as
      | Record<string, unknown>
      | undefined;
    return {
      email: u.email,
      familyName:
        (raw?.["family_name"] as string) ??
        (raw?.["last_name"] as string) ??
        null,
      givenName:
        (raw?.["given_name"] as string) ??
        (raw?.["first_name"] as string) ??
        null,
      id: u.uid,
      name: u.displayName ?? (raw?.["name"] as string) ?? null,
      imageUrl: u.photoUrl ?? (raw?.["picture"] as string) ?? null,
    };
  }

  private toDebugSignInSnapshot(result: SignInResult): Record<string, unknown> {
    return {
      hasUser: !!result.user,
      uid: result.user?.uid ?? null,
      email: result.user?.email ?? null,
      providerId: result.credential?.providerId ?? null,
      hasCredentialIdToken:
        typeof result.credential?.idToken === "string" &&
        result.credential.idToken.length > 0,
    };
  }

  private toDebugPayloadSnapshot(
    payload: FirebaseGoogleSignInPayload
  ): Record<string, unknown> {
    return {
      tokenLength: payload.idToken.length,
      tokenPreview: `${payload.idToken.slice(0, 12)}...`,
      profile: {
        email: payload.profile.email,
        name: payload.profile.name,
        id: payload.profile.id,
      },
    };
  }
}
